// Writing a note, or correcting one you wrote.
//
// THE RECIPIENT IS CHOSEN ONCE, WHEN THE NOTE IS WRITTEN, and the field is not offered
// again on an edit — re-addressing a note to somebody else is not a correction, it is a
// different note, and the server's UPDATE touches title and body alone. Showing a picker
// that silently did nothing would be worse than showing none.
//
// It opens on YOURSELF, so "a note to myself" costs no decision at all. Choosing somebody
// else is one field, and it is the only thing that distinguishes the two cases — there is
// no mode and no second kind of row behind it.
//
// ⚠ THE PICKER IS `usersApi.assignable()`, NEVER `authApi.partners()`. Those answer two
// different questions: partners are the MONEY identities (who may issue an invoice, pay
// an expense, hold a share), and a note attributes nothing — the secretary writes them,
// reads them and is written to. `assignable` is also the list that already excludes a
// content writer, who reaches one upload screen and would never see a note left for them;
// the server refuses that recipient too (`invalid_note_recipient`).
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import { useAuth } from '../../context/AuthContext';
import { notesApi, usersApi } from '../../api';
import he from '../../i18n/he';
import Modal from '../../components/Modal';
import { TextInput, Textarea, Select, Field } from '../../components/Form';
import { ErrorText } from '../../components/Loader';

export default function NoteModal({ note, onClose, onSaved }) {
  const { partner: me } = useAuth();
  const { data } = useAsync(() => usersApi.assignable());
  const users = data || [];
  const isNew = !note.id;

  const [form, setForm] = useState({
    title: note.title || '',
    body: note.body || '',
    // Seeded with the session user rather than left empty: the common case is a note to
    // yourself, and an empty required picker would ask a question that usually has one
    // obvious answer.
    for_user_id: note.for_user_id || me.id,
  });
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  // You first — it is the default, so it is the option the eye should land on — and your
  // own name is marked as yours, because on a two-partner board the names alone do not
  // say which one is the person reading the screen.
  const userOptions = [...users]
    .sort((a, b) => Number(b.id === me.id) - Number(a.id === me.id))
    .map((u) => ({ value: u.id, label: u.id === me.id ? he.notes.forMe(u.name) : u.name }));

  const submit = async () => {
    try {
      if (isNew) {
        await notesApi.create({
          title: form.title,
          body: form.body,
          for_user_id: Number(form.for_user_id) || me.id,
        });
      } else {
        // Title and body only, matching what the server will accept. Sending the
        // recipient back would be a value the API ignores — and a payload that claims
        // more than it does is how a later change starts honouring it by accident.
        await notesApi.update(note.id, { title: form.title, body: form.body });
      }
      onSaved();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Modal
      title={isNew ? he.notes.newNote : he.notes.editNote}
      onClose={onClose}
      onSave={submit}
      // The same two fields the server requires, so Save is refused here for the reason
      // it would be refused there rather than by a second, looser rule.
      saveDisabled={!form.title.trim() || !form.body.trim()}
    >
      {isNew ? (
        <Select
          label={he.notes.forUser}
          value={form.for_user_id}
          onChange={set('for_user_id')}
          options={userOptions}
        />
      ) : (
        // Shown, not hidden: whose board this note sits on is the first thing you need
        // to know while editing it, and reading it here is what stops an edit being made
        // in the belief it will move.
        <Field label={he.notes.forUser}>
          <div className="muted">{note.for_user_name}</div>
        </Field>
      )}

      <TextInput label={he.notes.noteTitle} value={form.title} onChange={set('title')} />
      <Textarea
        label={he.notes.noteBody}
        value={form.body}
        onChange={set('body')}
        placeholder={he.notes.bodyPlaceholder}
        rows={6}
      />

      {error && <ErrorText error={error} />}
    </Modal>
  );
}
